import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TopNav } from '../components/layout/TopNav';
import { BottomNav } from '../components/layout/BottomNav';
import { useCart } from '../hooks/useCart';
import { Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { TrustBadge } from '../components/ui/TrustBadge';
import { Link } from 'react-router-dom';
const itemVariants = {
  hidden: {
    opacity: 0,
    x: -20,
    height: 0
  },
  visible: {
    opacity: 1,
    x: 0,
    height: 'auto',
    transition: {
      type: 'spring',
      damping: 25,
      stiffness: 300
    }
  },
  exit: {
    opacity: 0,
    x: -20,
    height: 0,
    transition: {
      duration: 0.3
    }
  }
};
export function CartPage() {
  const {
    items,
    updateQuantity,
    removeFromCart,
    totalPrice,
    totalItems
  } = useCart();
  const handleQuantityChange = (id: string, currentQuantity: number, delta: number) => {
    const newQuantity = currentQuantity + delta;
    if (newQuantity > 0) {
      updateQuantity(id, newQuantity);
    }
  };
  return <div className="min-h-screen bg-[#FAF8F5] pb-24 md:pb-8" data-id="element-270">
      <TopNav data-id="element-271" />

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8" data-id="element-272">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="mb-6" data-id="element-273">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-274">
            Shopping Cart
          </h1>
          <p className="text-gray-600" data-id="element-275">
            {totalItems} {totalItems === 1 ? 'item' : 'items'} in your cart
          </p>
        </motion.div>

        {items.length === 0 ? <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center py-16" data-id="element-276">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-277">
              <ShoppingBag className="w-10 h-10 text-gray-400" data-id="element-278" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-279">
              Your cart is empty
            </h3>
            <p className="text-gray-600 mb-6" data-id="element-280">
              Add some products to get started
            </p>
            <Link to="/search" className="inline-flex items-center gap-2 bg-[#ff6b35] text-white font-semibold px-6 py-3 rounded-xl hover:bg-[#e85a2a] transition-colors" data-id="element-281">
              Start Shopping
            </Link>
          </motion.div> : <div className="grid lg:grid-cols-3 gap-6" data-id="element-282">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4" data-id="element-283">
              <AnimatePresence mode="popLayout" data-id="element-284">
                {items.map((item, index) => <motion.div key={item.id} variants={itemVariants} initial="hidden" animate="visible" exit="exit" layout transition={{
              layout: {
                type: 'spring',
                damping: 25,
                stiffness: 300
              },
              delay: index * 0.08
            }} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 md:p-6 overflow-hidden" data-id="element-285">
                    <div className="flex gap-4" data-id="element-286">
                      <div className="w-24 h-24 md:w-28 md:h-28 rounded-xl overflow-hidden bg-gray-50 flex-shrink-0" data-id="element-287">
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-288" />
                      </div>

                      <div className="flex-grow min-w-0" data-id="element-289">
                        <div className="flex items-start justify-between gap-2 mb-2" data-id="element-290">
                          <div className="flex-grow min-w-0" data-id="element-291">
                            <h3 className="font-bold text-gray-900 text-base md:text-lg mb-1 line-clamp-2" data-id="element-292">
                              {item.name}
                            </h3>
                            <p className="text-sm text-gray-500 mb-2" data-id="element-293">
                              {item.category}
                            </p>
                            <div className="flex items-center gap-2" data-id="element-294">
                              {item.isWIC && <TrustBadge type="WIC" size="sm" data-id="element-295" />}
                              {item.isEBT && <TrustBadge type="EBT" size="sm" data-id="element-296" />}
                            </div>
                          </div>

                          <button onClick={() => removeFromCart(item.id)} className="p-2 hover:bg-red-50 rounded-lg transition-colors text-red-600 flex-shrink-0" aria-label="Remove item" data-id="element-297">
                            <Trash2 className="w-5 h-5" data-id="element-298" />
                          </button>
                        </div>

                        <div className="flex items-center justify-between mt-4" data-id="element-299">
                          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1" data-id="element-300">
                            <button onClick={() => handleQuantityChange(item.id, item.quantity, -1)} className="p-1.5 hover:bg-white rounded-md transition-colors" aria-label="Decrease quantity" data-id="element-301">
                              <Minus className="w-4 h-4 text-gray-700" data-id="element-302" />
                            </button>
                            <motion.span key={item.quantity} initial={{
                        scale: 1.2
                      }} animate={{
                        scale: 1
                      }} className="w-8 text-center font-semibold text-gray-900" data-id="element-303">
                              {item.quantity}
                            </motion.span>
                            <button onClick={() => handleQuantityChange(item.id, item.quantity, 1)} className="p-1.5 hover:bg-white rounded-md transition-colors" aria-label="Increase quantity" data-id="element-304">
                              <Plus className="w-4 h-4 text-gray-700" data-id="element-305" />
                            </button>
                          </div>

                          <div className="text-right" data-id="element-306">
                            <div className="text-sm text-gray-500" data-id="element-307">
                              ${item.price.toFixed(2)} each
                            </div>
                            <div className="text-lg font-bold text-[#1e3a8a]" data-id="element-308">
                              ${(item.price * item.quantity).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>)}
              </AnimatePresence>
            </div>

            {/* Order Summary */}
            <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="lg:col-span-1" data-id="element-309">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sticky top-24" data-id="element-310">
                <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-311">
                  Order Summary
                </h2>

                <div className="space-y-3 mb-6 pb-6 border-b border-gray-200" data-id="element-312">
                  <div className="flex justify-between text-gray-600" data-id="element-313">
                    <span data-id="element-314">Subtotal ({totalItems} items)</span>
                    <span className="font-medium" data-id="element-315">
                      ${totalPrice.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-gray-600" data-id="element-316">
                    <span data-id="element-317">Delivery Fee</span>
                    <span className="font-medium text-[#ff6b35]" data-id="element-318">
                      {totalPrice >= 35 ? 'FREE' : '$4.99'}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between text-lg font-bold text-gray-900 mb-6" data-id="element-319">
                  <span data-id="element-320">Total</span>
                  <span className="text-[#1e3a8a]" data-id="element-321">
                    $
                    {(totalPrice >= 35 ? totalPrice : totalPrice + 4.99).toFixed(2)}
                  </span>
                </div>

                {totalPrice < 35 && <div className="bg-[#ff6b35]/10 border border-[#ff6b35]/20 rounded-xl p-4 mb-4" data-id="element-322">
                    <p className="text-sm text-gray-700" data-id="element-323">
                      Add{' '}
                      <span className="font-bold text-[#ff6b35]" data-id="element-324">
                        ${(35 - totalPrice).toFixed(2)}
                      </span>{' '}
                      more for free delivery!
                    </p>
                  </div>}

                <button className="w-full bg-[#ff6b35] text-white font-bold py-4 px-6 rounded-xl hover:bg-[#e85a2a] transition-colors shadow-lg hover:shadow-xl" data-id="element-325">
                  Proceed to Checkout
                </button>

                <Link to="/search" className="block text-center text-[#ff6b35] font-semibold mt-4 hover:text-[#e85a2a] transition-colors" data-id="element-326">
                  Continue Shopping
                </Link>
              </div>
            </motion.div>
          </div>}
      </div>

      <BottomNav data-id="element-327" />
    </div>;
}